import {
  CheckCircleOutline,
  CompareArrowsOutlined,
  DeleteOutline,
  EditOutlined,
  NumbersOutlined,
  RestartAltOutlined,
  SettingsSuggestOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  NumberingPolicy,
  NumberingValidation,
  ConfigurationProfileCode,
  HandlingEquipment,
  PutawayWorkQueue,
  activateNumberingPolicy,
  applyConfigurationProfile,
  changePutawayWorkQueueStatus,
  createPutawayWorkQueue,
  createHandlingEquipment,
  deleteHandlingEquipment,
  deletePutawayWorkQueue,
  loadApplicationSettings,
  loadConfigurationProfiles,
  loadCurrentConfigurationProfile,
  loadAllPutawayWorkQueues,
  loadHandlingEquipment,
  loadInventoryAbcPolicy,
  loadNumberingPolicies,
  loadPutawayWorkQueues,
  loadWarehouseStructure,
  previewConfigurationProfile,
  updateApplicationSetting,
  updateHandlingEquipment,
  updatePutawayWorkQueue,
  saveInventoryAbcPolicy,
  validateNumberingPolicy
} from "./api";
import { PageHeader } from "./PageHeader";
import {
  formatMeasurement, measurementInputStep, measurementInputValue,
  measurementUnit, toMetric
} from "./measurements";
import { useMeasurementSettings } from "./useMeasurementSettings";
import { ReplenishmentOutputSettingsPanel } from "./ReplenishmentOutputSettingsPanel";
import { BusinessPartnerSettingsPanel } from "./BusinessPartnerSettingsPanel";

export function NumberingConfigurationPage({ onBack, canConfigureReplenishment = false }: { onBack: () => void; canConfigureReplenishment?: boolean }) {
  const { t } = useTranslation();
  const labels: Record<NumberingPolicy["documentType"], string> = {
    ASN: t("configuration.numbering.asn"),
    RECEIPT: t("configuration.numbering.receipt"),
    HANDLING_UNIT: t("configuration.numbering.hu"),
    INVENTORY_COUNT: t("configuration.numbering.inventoryCount")
  };
  const [selected, setSelected] = useState<NumberingPolicy | null>(null);
  const [section, setSection] = useState("organization");
  const [subsection, setSubsection] = useState("profiles");
  const policies = useQuery({
    queryKey: ["numbering-policies"], queryFn: loadNumberingPolicies
  });
  const navigation = [
    { value: "organization", label: t("configuration.navigation.organization"), children: [
      { value: "profiles", label: t("configuration.navigation.profiles") },
      { value: "measurements", label: t("configuration.navigation.measurements") }
    ] },
    { value: "references", label: t("configuration.navigation.references"), children: [
      { value: "numbering", label: t("configuration.navigation.numbering") },
      { value: "equipment", label: t("configuration.navigation.equipment") },
      { value: "businessPartners", label: t("configuration.navigation.businessPartners") }
    ] },
    { value: "stock", label: t("configuration.navigation.stockAllocation"), children: [
      { value: "allocations", label: t("configuration.navigation.stockReservation") },
      { value: "inventory", label: t("configuration.navigation.inventoryAbc") }
    ] },
    { value: "inbound", label: t("configuration.navigation.inboundPutaway"), children: [
      { value: "putaway", label: t("configuration.navigation.putaway") },
      { value: "putawayQueues", label: t("configuration.navigation.putawayQueues") }
    ] },
    { value: "quality", label: t("configuration.navigation.qualityCompliance"), children: [
      { value: "quality", label: t("configuration.navigation.derogations") }
    ] },
    { value: "outbound", label: t("configuration.navigation.outboundPreparation"), children: [
      { value: "outbound", label: t("configuration.navigation.orderAllocation") },
      { value: "picking", label: t("configuration.navigation.preparation") }
    ] },
    { value: "transfers", label: t("configuration.navigation.internalTransfers"), children: [
      { value: "transfers", label: t("configuration.navigation.transferRules") }
    ] },
    { value: "experience", label: t("configuration.navigation.userExperience"), children: [
      { value: "activities", label: t("configuration.navigation.dashboardOpening") }
    ] },
    ...(canConfigureReplenishment ? [{
      value: "replenishment", label: t("configuration.navigation.replenishment"), children: [
        { value: "replenishmentOutputs", label: t("configuration.navigation.outputProfiles") }
      ]
    }] : [])
  ];
  const currentNavigation = navigation.find((entry) => entry.value === section)
    ?? navigation[0];

  return (
    <Stack spacing={1.5} sx={{
      height: { md: "100%" }, minHeight: 0
    }}>
      <PageHeader
        title={t("configuration.title")}
        compact
        onBack={onBack}
      />
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" },
        gap: 1.5, minHeight: 0, flex: { md: 1 } }}>
      <Tabs orientation="vertical" value={section} onChange={(_, value) => {
        const next = navigation.find((entry) => entry.value === value);
        setSection(value);
        if (next) setSubsection(next.children[0].value);
      }}
        variant="scrollable" sx={{ display: { xs: "none", md: "flex" },
          width: 250, flexShrink: 0, borderRight: 1, borderColor: "divider",
          "& .MuiTab-root": { alignItems: "flex-start", textAlign: "left",
            textTransform: "none", minHeight: 48 } }}>
        {navigation.map((entry) => <Tab key={entry.value}
          value={entry.value} label={entry.label} />)}
      </Tabs>
      <Tabs value={section} onChange={(_, value) => {
        const next = navigation.find((entry) => entry.value === value);
        setSection(value);
        if (next) setSubsection(next.children[0].value);
      }} variant="scrollable" scrollButtons="auto"
        sx={{ display: { xs: "flex", md: "none" } }}>
        {navigation.map((entry) => <Tab key={entry.value}
          value={entry.value} label={entry.label} />)}
      </Tabs>
      <Box sx={{ minWidth: 0, minHeight: 0, flex: 1,
        display: "flex", flexDirection: "column" }}>
      <Tabs value={subsection} onChange={(_, value) => setSubsection(value)}
        variant="scrollable" scrollButtons="auto" sx={{ minHeight: 40,
          borderBottom: 1, borderColor: "divider",
          "& .MuiTab-root": { minHeight: 40, textTransform: "none" } }}>
        {currentNavigation.children.map((entry) => <Tab key={entry.value}
          value={entry.value} label={entry.label} />)}
      </Tabs>
      <Box sx={{
        minHeight: 0, flex: { md: 1 }, overflowY: { md: "auto" },
        pr: { md: .5 }
      }}>
      {subsection === "numbering" ? (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("configuration.numbering.info")}
          </Alert>
          {policies.isError && (
            <Alert severity="error">{t("configuration.numbering.loadError")}</Alert>
          )}
          <Stack spacing={1.5}>
            {policies.data?.map((policy) => (
              <Card key={policy.id} variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}
                    alignItems={{ sm: "center" }}>
                    <NumbersOutlined color="secondary" />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography fontWeight={800}>
                        {labels[policy.documentType]}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("configuration.numbering.version", { version: policy.versionNumber })}
                        {policy.automatic
                          ? ` · ${t("configuration.numbering.nextPreview", { preview: policy.preview, start: policy.rangeStart, end: policy.rangeEnd })}`
                          : ` · ${t("configuration.numbering.manualEntry")}`}
                      </Typography>
                    </Box>
                    <Chip color={policy.automatic ? "success" : "default"}
                      label={policy.automatic ? t("configuration.common.automatic") : t("configuration.common.manual")} />
                    <Button variant="outlined" startIcon={<EditOutlined />}
                      onClick={() => setSelected(policy)}>
                      {t("configuration.common.configure")}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </>
      ) : subsection === "profiles" ? <ConfigurationProfilePanel />
        : subsection === "measurements" ? <MeasurementSettingsPanel />
        : subsection === "allocations" ? <AllocationSettingsPanel />
        : subsection === "putaway" ? <PutawaySettingsPanel />
        : subsection === "putawayQueues" ? <PutawayQueueSettings />
        : subsection === "quality" ? <QualitySettingsPanel />
        : subsection === "picking" ? <PickingSettingsPanel />
        : subsection === "outbound" ? <OutboundOrderSettingsPanel />
        : subsection === "equipment" ? <Stack spacing={1.5}>
            <HandlingEquipmentSettings /><EquipmentReleaseSettingsPanel />
          </Stack>
        : subsection === "businessPartners" ? <BusinessPartnerSettingsPanel />
        : subsection === "inventory" ? <InventoryAbcSettingsPanel />
        : subsection === "transfers" ? <TransferSettingsPanel />
        : subsection === "replenishmentOutputs" ? <ReplenishmentOutputSettingsPanel />
        : <ActivitySettingsPanel />}
      </Box>
      </Box>
      </Box>
      <NumberingPolicyDialog policy={selected}
        onClose={() => setSelected(null)} />
    </Stack>
  );
}

function MeasurementSettingsPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ["application-settings"], queryFn: loadApplicationSettings
  });
  const measurementSettings = settings.data?.filter((setting) =>
    setting.category === "MEASUREMENTS") ?? [];
  const [values, setValues] = useState<Record<string, string>>({});
  useEffect(() => {
    if (measurementSettings.length > 0) setValues(Object.fromEntries(
      measurementSettings.map((setting) => [setting.key, setting.value])
    ));
  }, [settings.data]);
  const save = useMutation({
    mutationFn: () => Promise.all(measurementSettings.map((setting) =>
      values[setting.key] === setting.value ? Promise.resolve(setting)
        : updateApplicationSetting(setting.key, values[setting.key] ?? setting.value, setting.version)
    )),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["application-settings"] })
  });
  const numericKeys = [
    "LENGTH_MM_PER_INCH", "MASS_KG_PER_POUND",
    "VOLUME_MM3_PER_CUBIC_INCH", "MEASUREMENT_DISPLAY_DECIMALS"
  ];
  const invalid = numericKeys.some((key) => {
    const value = Number(values[key]);
    return !Number.isFinite(value) || (key === "MEASUREMENT_DISPLAY_DECIMALS"
      ? !Number.isInteger(value) || value < 0 || value > 6 : value <= 0);
  });
  return <Stack spacing={2}>
    <Alert severity="info">{t("configuration.measurements.canonicalHelp")}</Alert>
    {settings.isError && <Alert severity="error">{t("configuration.measurements.loadError")}</Alert>}
    <Card variant="outlined"><CardContent>
      <Stack spacing={2}>
        <Typography variant="h6" fontWeight={800}>{t("configuration.measurements.title")}</Typography>
        <FormControl fullWidth>
          <InputLabel>{t("configuration.measurements.system")}</InputLabel>
          <Select value={values.MEASUREMENT_SYSTEM ?? "METRIC"}
            label={t("configuration.measurements.system")}
            onChange={(event) => setValues((current) => ({
              ...current, MEASUREMENT_SYSTEM: event.target.value
            }))}>
            <MenuItem value="METRIC">{t("configuration.measurements.metric")}</MenuItem>
            <MenuItem value="IMPERIAL">{t("configuration.measurements.imperial")}</MenuItem>
          </Select>
        </FormControl>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField fullWidth type="number" value={values.LENGTH_MM_PER_INCH ?? ""}
            label={t("configuration.measurements.mmPerInch")}
            onChange={(event) => setValues((current) => ({ ...current,
              LENGTH_MM_PER_INCH: event.target.value }))} />
          <TextField fullWidth type="number" value={values.MASS_KG_PER_POUND ?? ""}
            label={t("configuration.measurements.kgPerPound")}
            onChange={(event) => setValues((current) => ({ ...current,
              MASS_KG_PER_POUND: event.target.value }))} />
          <TextField fullWidth type="number" value={values.VOLUME_MM3_PER_CUBIC_INCH ?? ""}
            label={t("configuration.measurements.mm3PerIn3")}
            onChange={(event) => setValues((current) => ({ ...current,
              VOLUME_MM3_PER_CUBIC_INCH: event.target.value }))} />
        </Stack>
        <TextField type="number" value={values.MEASUREMENT_DISPLAY_DECIMALS ?? "2"}
          label={t("configuration.measurements.decimals")}
          slotProps={{ htmlInput: { min: 0, max: 6, step: 1 } }}
          onChange={(event) => setValues((current) => ({ ...current,
            MEASUREMENT_DISPLAY_DECIMALS: event.target.value }))} />
        {save.isError && <Alert severity="error">{t("configuration.measurements.saveError")}</Alert>}
        {save.isSuccess && <Alert severity="success">{t("configuration.measurements.saved")}</Alert>}
        <Button variant="contained" onClick={() => save.mutate()}
          disabled={settings.isLoading || save.isPending || invalid}>
          {t("configuration.common.save")}
        </Button>
      </Stack>
    </CardContent></Card>
  </Stack>;
}

function ConfigurationProfilePanel() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const profiles = useQuery({
    queryKey: ["configuration-profiles"], queryFn: loadConfigurationProfiles
  });
  const current = useQuery({
    queryKey: ["configuration-profile-current"],
    queryFn: loadCurrentConfigurationProfile
  });
  const settings = useQuery({
    queryKey: ["application-settings"], queryFn: loadApplicationSettings
  });
  const [selectedCode, setSelectedCode] = useState<ConfigurationProfileCode>("STANDARD");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewCode, setPreviewCode] = useState<ConfigurationProfileCode | null>(null);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [comparisonFilter, setComparisonFilter] = useState<"all" | "different">("different");
  const [leftCode, setLeftCode] = useState<ConfigurationProfileCode>("STANDARD");
  const [rightCode, setRightCode] = useState<ConfigurationProfileCode>("AERONAUTIQUE");
  useEffect(() => {
    if (current.data?.profile) setSelectedCode(current.data.profile);
  }, [current.data?.profile]);
  const preview = useQuery({
    queryKey: ["configuration-profile-preview", previewCode],
    queryFn: () => previewConfigurationProfile(previewCode!),
    enabled: previewCode !== null
  });
  const apply = useMutation({
    mutationFn: () => applyConfigurationProfile(
      preview.data!.profile, preview.data!.profileVersion
    ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["application-settings"] }),
        queryClient.invalidateQueries({ queryKey: ["configuration-profile-current"] }),
        queryClient.invalidateQueries({ queryKey: ["configuration-profile-preview"] })
      ]);
      setPreviewOpen(false);
    }
  });
  const closePreview = () => {
    if (apply.isPending) return;
    setPreviewOpen(false);
    apply.reset();
  };
  const profileLabel = (code: ConfigurationProfileCode) =>
    t(`configuration.profile.codes.${code}.label`);
  const displayNames = new Map(settings.data?.map((entry) => [entry.key, entry.displayName]));
  const profileComparison = useMemo(() => {
    const left = profiles.data?.find((profile) => profile.code === leftCode);
    const right = profiles.data?.find((profile) => profile.code === rightCode);
    if (!left || !right) return [];
    return Object.keys(left.values).sort().map((key) => ({
      key,
      leftValue: left.values[key],
      rightValue: right.values[key],
      different: left.values[key] !== right.values[key]
    }));
  }, [leftCode, profiles.data, rightCode]);
  const profileDifferences = profileComparison.filter((entry) => entry.different);
  const displayedProfileSettings = comparisonFilter === "different"
    ? profileDifferences : profileComparison;
  const loadingError = profiles.isError || current.isError || settings.isError;
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ p: { xs: 2, md: 2.5 }, "&:last-child": { pb: { xs: 2, md: 2.5 } } }}>
        <Stack spacing={1.25}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}
            alignItems={{ md: "center" }}>
            <SettingsSuggestOutlined color="secondary" />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" fontWeight={800}>
                {t("configuration.profile.title")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("configuration.profile.description")}
              </Typography>
            </Box>
            {current.data ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip color="primary" label={profileLabel(current.data.profile)} />
                <Chip variant="outlined" label={t("configuration.profile.version", {
                  version: current.data.profileVersion
                })} />
                <Chip color={current.data.changedSettingCount ? "warning" : "success"}
                  variant="outlined" label={current.data.changedSettingCount
                    ? t("configuration.profile.drift", { count: current.data.changedSettingCount })
                    : t("configuration.profile.unchanged")} />
              </Stack>
            ) : <Chip label={t("configuration.profile.noneApplied")} />}
          </Stack>
          {current.data && (
            <Typography variant="caption" color="text.secondary">
              {t("configuration.profile.applied", {
                date: new Intl.DateTimeFormat(i18n.language, {
                  dateStyle: "medium", timeStyle: "short"
                }).format(new Date(current.data.appliedAt)),
                actor: current.data.appliedBy ?? t("configuration.profile.unknownActor")
              })}
            </Typography>
          )}
          {loadingError && <Alert severity="error" sx={{ py: .25 }}>
            {t("configuration.profile.loadError")}
          </Alert>}
          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(320px, 720px) auto auto" },
            gap: 1.5,
            alignItems: "stretch"
          }}>
            <FormControl>
              <InputLabel>{t("configuration.profile.choose")}</InputLabel>
              <Select value={selectedCode} label={t("configuration.profile.choose")}
                onChange={(event) => setSelectedCode(event.target.value as ConfigurationProfileCode)}>
                {profiles.data?.map((profile) => (
                  <MenuItem key={profile.code} value={profile.code}>
                    {profileLabel(profile.code)} · {t("configuration.profile.version", {
                      version: profile.version
                    })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" startIcon={<RestartAltOutlined />}
              disabled={profiles.isLoading || !profiles.data?.length}
              onClick={() => {
                apply.reset();
                if (previewCode === selectedCode) void preview.refetch();
                else setPreviewCode(selectedCode);
                setPreviewOpen(true);
              }} sx={{
                whiteSpace: "nowrap", minWidth: { md: 190 }, px: 2.5
              }}>
              {t("configuration.profile.previewAction")}
            </Button>
            <Button variant="outlined" startIcon={<CompareArrowsOutlined />}
              disabled={profiles.isLoading || (profiles.data?.length ?? 0) < 2}
              onClick={() => setComparisonOpen(true)} sx={{
                whiteSpace: "nowrap", minWidth: { md: 260 }, px: 2.5
              }}>
              {t("configuration.profile.compareAction")}
            </Button>
          </Box>
        </Stack>
      </CardContent>
      <Dialog open={previewOpen} onClose={closePreview}
        fullWidth maxWidth="md">
        <DialogTitle>{t("configuration.profile.previewTitle", {
          profile: profileLabel(previewCode ?? selectedCode)
        })}</DialogTitle>
        <DialogContent dividers>
          {preview.isLoading && <Typography>{t("configuration.profile.loadingPreview")}</Typography>}
          {preview.isError && <Alert severity="error">{t("configuration.profile.previewError")}</Alert>}
          {preview.data && (
            <Stack spacing={1.5}>
              <Alert severity={preview.data.changedSettingCount ? "warning" : "success"}>
                {preview.data.changedSettingCount
                  ? t("configuration.profile.changeCount", { count: preview.data.changedSettingCount })
                  : t("configuration.profile.noChanges")}
              </Alert>
              {preview.data.changes.map((change) => (
                <Card key={change.key} variant="outlined"><CardContent>
                  <Typography fontWeight={800}>
                    {displayNames.get(change.key) ?? change.key}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                    {change.key}
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mt={1}>
                    <Chip variant="outlined" label={t("configuration.profile.currentValue", {
                      value: change.currentValue
                    })} />
                    <Chip color="primary" variant="outlined" label={t("configuration.profile.presetValue", {
                      value: change.presetValue
                    })} />
                  </Stack>
                </CardContent></Card>
              ))}
              <Alert severity="info">{t("configuration.profile.applyWarning")}</Alert>
              {apply.isError && <Alert severity="error">{t("configuration.profile.applyError")}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview} disabled={apply.isPending}>
            {t("configuration.common.cancel")}
          </Button>
          <Button variant="contained" onClick={() => apply.mutate()}
            disabled={!preview.data || preview.data.changedSettingCount === 0 || apply.isPending}>
            {apply.isPending ? t("configuration.profile.applying") : t("configuration.profile.apply")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={comparisonOpen} onClose={() => setComparisonOpen(false)}
        fullWidth maxWidth="md">
        <DialogTitle>{t("configuration.profile.compareTitle")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t("configuration.profile.compareDescription")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}
              alignItems={{ sm: "center" }}>
              <FormControl fullWidth>
                <InputLabel>{t("configuration.profile.sourceProfile")}</InputLabel>
                <Select value={leftCode} label={t("configuration.profile.sourceProfile")}
                  onChange={(event) => setLeftCode(event.target.value as ConfigurationProfileCode)}>
                  {profiles.data?.map((profile) => (
                    <MenuItem key={profile.code} value={profile.code}>{profileLabel(profile.code)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <CompareArrowsOutlined color="action" />
              <FormControl fullWidth>
                <InputLabel>{t("configuration.profile.targetProfile")}</InputLabel>
                <Select value={rightCode} label={t("configuration.profile.targetProfile")}
                  onChange={(event) => setRightCode(event.target.value as ConfigurationProfileCode)}>
                  {profiles.data?.map((profile) => (
                    <MenuItem key={profile.code} value={profile.code}>{profileLabel(profile.code)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Alert severity={profileDifferences.length ? "info" : "success"}>
              {profileDifferences.length
                ? t("configuration.profile.profileDifferenceCount", { count: profileDifferences.length })
                : t("configuration.profile.identicalProfiles")}
            </Alert>
            <ToggleButtonGroup value={comparisonFilter} exclusive size="small"
              onChange={(_, value: "all" | "different" | null) => {
                if (value) setComparisonFilter(value);
              }} aria-label={t("configuration.profile.filterLabel")}>
              <ToggleButton value="all">
                {t("configuration.profile.allSettings", { count: profileComparison.length })}
              </ToggleButton>
              <ToggleButton value="different">
                {t("configuration.profile.differentSettings", { count: profileDifferences.length })}
              </ToggleButton>
            </ToggleButtonGroup>
            {displayedProfileSettings.map((difference) => (
              <Card key={difference.key} variant="outlined"><CardContent>
                <Typography fontWeight={800}>
                  {displayNames.get(difference.key) ?? difference.key}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                  {difference.key}
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mt={1}>
                  <Chip variant="outlined" label={`${profileLabel(leftCode)} : ${difference.leftValue}`} />
                  <Chip color={difference.different ? "secondary" : "default"} variant="outlined"
                    label={`${profileLabel(rightCode)} : ${difference.rightValue}`} />
                </Stack>
              </CardContent></Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComparisonOpen(false)}>
            {t("configuration.profile.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

function InventoryAbcSettingsPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const structure = useQuery({
    queryKey: ["warehouse-structure"], queryFn: loadWarehouseStructure
  });
  const warehouses = (structure.data ?? []).filter((node) =>
    node.type === "WAREHOUSE" && node.status === "ACTIVE");
  const [warehouseId, setWarehouseId] = useState("");
  const effectiveWarehouseId = warehouseId || warehouses[0]?.id || "";
  const policy = useQuery({
    queryKey: ["inventory-abc-policy", effectiveWarehouseId],
    queryFn: () => loadInventoryAbcPolicy(effectiveWarehouseId),
    enabled: Boolean(effectiveWarehouseId)
  });
  const [frequencyA, setFrequencyA] = useState("90");
  const [frequencyB, setFrequencyB] = useState("180");
  const [frequencyC, setFrequencyC] = useState("365");
  useEffect(() => {
    if (!policy.data) return;
    setFrequencyA(String(policy.data.classAFrequencyDays));
    setFrequencyB(String(policy.data.classBFrequencyDays));
    setFrequencyC(String(policy.data.classCFrequencyDays));
  }, [policy.data]);
  const valid = [frequencyA, frequencyB, frequencyC].every((value) =>
    Number.isInteger(Number(value)) && Number(value) > 0);
  const save = useMutation({
    mutationFn: () => saveInventoryAbcPolicy(effectiveWarehouseId, {
      classAFrequencyDays: Number(frequencyA),
      classBFrequencyDays: Number(frequencyB),
      classCFrequencyDays: Number(frequencyC)
    }),
    onSuccess: async () => queryClient.invalidateQueries({
      queryKey: ["inventory-abc-policy", effectiveWarehouseId]
    })
  });
  return (
    <Stack spacing={2}>
      <Alert severity="info">{t("configuration.inventoryAbc.description")}</Alert>
      {(structure.isError || policy.isError) && (
        <Alert severity="error">{t("configuration.inventoryAbc.loadError")}</Alert>
      )}
      <Card variant="outlined"><CardContent>
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={800}>
            {t("configuration.inventoryAbc.title")}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>{t("configuration.inventoryAbc.warehouse")}</InputLabel>
            <Select value={effectiveWarehouseId}
              label={t("configuration.inventoryAbc.warehouse")}
              onChange={(event) => setWarehouseId(event.target.value)}>
              {warehouses.map((warehouse) => (
                <MenuItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.code} · {warehouse.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            {(["A", "B", "C"] as const).map((abcClass) => {
              const value = abcClass === "A" ? frequencyA : abcClass === "B" ? frequencyB : frequencyC;
              const setter = abcClass === "A" ? setFrequencyA : abcClass === "B" ? setFrequencyB : setFrequencyC;
              return <TextField key={abcClass} type="number" fullWidth value={value}
                label={t("configuration.inventoryAbc.frequency", { abcClass })}
                inputProps={{ min: 1, step: 1 }} onChange={(event) => setter(event.target.value)} />;
            })}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t("configuration.inventoryAbc.help")}
          </Typography>
          {save.isSuccess && <Alert severity="success">{t("configuration.common.saved")}</Alert>}
          {save.isError && <Alert severity="error">{t("configuration.common.saveError")}</Alert>}
          <Button variant="contained" disabled={!effectiveWarehouseId || !valid || save.isPending}
            onClick={() => save.mutate()}>
            {t("configuration.common.save")}
          </Button>
        </Stack>
      </CardContent></Card>
    </Stack>
  );
}

function PutawaySettingsPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ["application-settings"],
    queryFn: loadApplicationSettings
  });
  const approvalSetting = settings.data?.find((entry) =>
    entry.key === "PUTAWAY_SIMULATION_APPROVAL_REQUIRED"
  );
  const reservationSetting = settings.data?.find((entry) =>
    entry.key === "PUTAWAY_RESERVATION_MINUTES"
  );
  const [approvalRequired, setApprovalRequired] = useState(true);
  const [reservationMinutes, setReservationMinutes] = useState("30");
  useEffect(() => {
    if (approvalSetting) {
      setApprovalRequired(approvalSetting.value === "true");
    }
  }, [approvalSetting]);
  useEffect(() => {
    if (reservationSetting) setReservationMinutes(reservationSetting.value);
  }, [reservationSetting]);
  const saveApproval = useMutation({
    mutationFn: () => updateApplicationSetting(
      approvalSetting!.key, String(approvalRequired), approvalSetting!.version
    ),
    onSuccess: async () => queryClient.invalidateQueries({
      queryKey: ["application-settings"]
    })
  });
  const parsedReservationMinutes = Number(reservationMinutes);
  const reservationIsValid = Number.isInteger(parsedReservationMinutes)
    && parsedReservationMinutes >= 0 && parsedReservationMinutes <= 525600;
  const saveReservation = useMutation({
    mutationFn: () => updateApplicationSetting(
      reservationSetting!.key, String(parsedReservationMinutes),
      reservationSetting!.version
    ),
    onSuccess: async () => queryClient.invalidateQueries({
      queryKey: ["application-settings"]
    })
  });

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">{t("configuration.allocation.validation")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("configuration.allocation.description")}
            </Typography>
          </Box>
          {settings.isError && (
            <Alert severity="error">
              {t("configuration.allocation.loadError")}
            </Alert>
          )}
          {approvalSetting && (
            <FormControlLabel
              control={<Switch checked={approvalRequired}
                onChange={(event) => setApprovalRequired(event.target.checked)} />}
              label={approvalRequired
                ? t("configuration.allocation.manualRequired")
                : t("configuration.allocation.automaticCreation")}
            />
          )}
          <Alert severity={approvalRequired ? "info" : "warning"}>
            {approvalRequired
              ? t("configuration.allocation.manualHelp")
              : t("configuration.allocation.automaticHelp")}
          </Alert>
          {saveApproval.isSuccess && (
            <Alert severity="success">{t("configuration.common.saved")}</Alert>
          )}
          {saveApproval.isError && (
            <Alert severity="error">{t("configuration.common.saveError")}</Alert>
          )}
          <Button variant="contained" sx={{ alignSelf: "flex-start" }}
            disabled={!approvalSetting
              || approvalRequired === (approvalSetting.value === "true")
              || saveApproval.isPending}
            onClick={() => saveApproval.mutate()}>
            {t("configuration.common.save")}
          </Button>
          <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
            <Typography variant="h6">{t("configuration.allocation.reservation")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("configuration.allocation.reservationHelp")}
            </Typography>
          </Box>
          {reservationSetting && (
            <TextField
              label={t("configuration.allocation.duration")}
              type="number"
              value={reservationMinutes}
              onChange={(event) => setReservationMinutes(event.target.value)}
              slotProps={{ htmlInput: { min: 0, max: 525600, step: 1 } }}
              error={!reservationIsValid}
              helperText={reservationIsValid
                ? t("configuration.allocation.noExpiry")
                : t("configuration.allocation.invalidDuration")}
              sx={{ maxWidth: 430 }}
            />
          )}
          {saveReservation.isSuccess && (
            <Alert severity="success">{t("configuration.allocation.durationSaved")}</Alert>
          )}
          {saveReservation.isError && (
            <Alert severity="error">
              {t("configuration.allocation.durationSaveError")}
            </Alert>
          )}
          <Button variant="contained" sx={{ alignSelf: "flex-start" }}
            disabled={!reservationSetting || !reservationIsValid
              || parsedReservationMinutes === Number(reservationSetting.value)
              || saveReservation.isPending}
            onClick={() => saveReservation.mutate()}>
            {t("configuration.allocation.saveDuration")}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function PutawayQueueSettings() {
  const { t, i18n } = useTranslation();
  const { configuration: measurements } = useMeasurementSettings();
  const locale = i18n.resolvedLanguage === "en" ? "en-US" : "fr-FR";
  const massUnit = measurementUnit("MASS", measurements);
  const volumeUnit = measurementUnit("VOLUME", measurements);
  const massStep = measurementInputStep("MASS", measurements);
  const volumeStep = measurementInputStep("VOLUME", measurements);
  const queryClient = useQueryClient();
  const queues = useQuery({
    queryKey: ["putaway-work-queues", "master-data"],
    queryFn: loadAllPutawayWorkQueues
  });
  const structure = useQuery({
    queryKey: ["warehouse-structure"], queryFn: loadWarehouseStructure
  });
  const [editing, setEditing] = useState<PutawayWorkQueue | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [warehouseId, setWarehouseId] = useState("");
  const [code, setCode] = useState("");
  const [equipmentTypeCode, setEquipmentTypeCode] = useState("");
  const [name, setName] = useState("");
  const [groupingMode, setGroupingMode] = useState<"SINGLE_HU" | "MULTI_HU">(
    "MULTI_HU"
  );
  const [maxHu, setMaxHu] = useState("12");
  const [maxWeight, setMaxWeight] = useState("");
  const [maxVolume, setMaxVolume] = useState("");
  const [routing, setRouting] = useState<"NONE" | "PRIORITY" | "LOCATION_SEQUENCE">(
    "LOCATION_SEQUENCE"
  );
  const [heavyThreshold, setHeavyThreshold] = useState("");
  const open = (queue?: PutawayWorkQueue) => {
    setDialogOpen(true);
    setEditing(queue ?? null);
    setWarehouseId(queue?.warehouseId ?? structure.data?.[0]?.id ?? "");
    setCode(queue?.code ?? "");
    setEquipmentTypeCode(queue?.equipmentTypeCode === "MANUAL"
      ? "ROLL" : queue?.equipmentTypeCode ?? "");
    setName(queue?.name ?? "");
    setGroupingMode(queue?.groupingMode ?? "MULTI_HU");
    setMaxHu(String(queue?.maxHandlingUnits ?? 12));
    setMaxWeight(queue?.maxWeightKg == null ? "" : measurementInputValue(
      queue.maxWeightKg, "MASS", measurements));
    setMaxVolume(queue?.maxVolumeMm3 == null ? "" : measurementInputValue(
      queue.maxVolumeMm3, "VOLUME", measurements));
    setRouting(queue?.routingStrategy ?? "LOCATION_SEQUENCE");
    setHeavyThreshold(queue?.heavyThresholdKg == null
      ? "" : measurementInputValue(queue.heavyThresholdKg, "MASS", measurements));
  };
  const saveQueue = useMutation({
    mutationFn: () => editing ? updatePutawayWorkQueue(editing, {
      name: name.trim(), groupingMode,
      maxHandlingUnits: groupingMode === "SINGLE_HU" ? 1 : Number(maxHu),
      maxWeightKg: maxWeight ? toMetric(Number(maxWeight), "MASS", measurements) : null,
      maxVolumeMm3: maxVolume ? toMetric(Number(maxVolume), "VOLUME", measurements) : null,
      routingStrategy: routing,
      heavyThresholdKg: heavyThreshold
        ? toMetric(Number(heavyThreshold), "MASS", measurements) : null
    }) : createPutawayWorkQueue({
      warehouseId, code: code.trim().toUpperCase(), name: name.trim(),
      equipmentTypeCode: equipmentTypeCode || null, groupingMode,
      maxHandlingUnits: groupingMode === "SINGLE_HU" ? 1 : Number(maxHu),
      maxWeightKg: maxWeight ? toMetric(Number(maxWeight), "MASS", measurements) : null,
      maxVolumeMm3: maxVolume ? toMetric(Number(maxVolume), "VOLUME", measurements) : null,
      routingStrategy: routing,
      heavyThresholdKg: heavyThreshold
        ? toMetric(Number(heavyThreshold), "MASS", measurements) : null
    }),
    onSuccess: async () => {
      setDialogOpen(false);
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ["putaway-work-queues"] });
    }
  });
  const statusQueue = useMutation({
    mutationFn: (queue: PutawayWorkQueue) => changePutawayWorkQueueStatus(
      queue, queue.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"
    ),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["putaway-work-queues"] })
  });
  const removeQueue = useMutation({
    mutationFn: deletePutawayWorkQueue,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["putaway-work-queues"] })
  });
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}
            justifyContent="space-between" alignItems={{ sm: "center" }}>
            <Box><Typography variant="h6">{t("configuration.queues.title")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("configuration.queues.description")}
            </Typography>
            </Box>
            <Button variant="contained" onClick={() => open()}>{t("configuration.queues.new")}</Button>
          </Stack>
          {queues.isError && (
            <Alert severity="error">{t("configuration.queues.loadError")}</Alert>
          )}
          {(queues.data ?? []).map((queue) => (
            <Box key={queue.id} sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr auto auto auto auto" },
              gap: 1.5, alignItems: "center", py: 1,
              borderTop: 1, borderColor: "divider"
            }}>
              <Box>
                <Typography fontWeight={800}>
                  {queue.code === "HEAVY" || queue.code === "PEDESTRIAN"
                    ? t(`putaway.queueNames.${queue.code}`)
                    : queue.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {queue.groupingMode === "SINGLE_HU"
                    ? t("configuration.queues.oneHu") : t("configuration.queues.huMaximum", { count: queue.maxHandlingUnits })}
                  {queue.maxWeightKg ? ` · ${formatMeasurement(
                    queue.maxWeightKg, "MASS", measurements, locale)} ${t(
                    "configuration.queues.maximum")}` : ""}
                  {queue.equipmentTypeCode ? ` · ${t("configuration.queues.equipment", { code: queue.equipmentTypeCode })}` : ""}
                  {" · "}{queue.routingStrategy === "LOCATION_SEQUENCE"
                    ? t("configuration.queues.optimized") : queue.routingStrategy === "PRIORITY"
                      ? t("configuration.queues.priority") : t("configuration.queues.none")}
                  {` · ${t("configuration.queues.waiting", { count: queue.waitingTaskCount })}`}
                </Typography>
              </Box>
              <Chip label={queue.code} variant="outlined" />
              <Chip label={queue.status === "ACTIVE" ? t("configuration.common.active") : t("configuration.common.blocked")}
                color={queue.status === "ACTIVE" ? "success" : "warning"} />
              <Button variant="outlined" onClick={() => open(queue)}>
                {t("configuration.common.edit")}
              </Button>
              <Stack direction="row" spacing={1}>
                <Button color={queue.status === "ACTIVE" ? "warning" : "success"}
                  onClick={() => statusQueue.mutate(queue)}>
                  {queue.status === "ACTIVE" ? t("configuration.common.block") : t("configuration.common.reactivate")}
                </Button>
                <Button color="error" onClick={() => {
                  if (window.confirm(t("configuration.common.deleteConfirm", { name: queue.name }))) {
                    removeQueue.mutate(queue.id);
                  }
                }}>{t("configuration.common.delete")}</Button>
              </Stack>
            </Box>
          ))}
          {(statusQueue.isError || removeQueue.isError) && (
            <Alert severity="error">
              {((statusQueue.error || removeQueue.error) instanceof Error)
                ? (statusQueue.error || removeQueue.error as Error)?.message
                : t("configuration.common.operationError")}
            </Alert>
          )}
        </Stack>
      </CardContent>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}
        fullWidth maxWidth="sm">
        <DialogTitle>{editing ? t("configuration.queues.edit", { name: editing.name }) : t("configuration.queues.create")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl disabled={Boolean(editing)}><InputLabel>{t("configuration.queues.warehouse")}</InputLabel>
              <Select label={t("configuration.queues.warehouse")} value={warehouseId}
                onChange={(event) => setWarehouseId(event.target.value)}>
                {(structure.data ?? []).map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.code} · {warehouse.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label={t("configuration.queues.code")} value={code} disabled={Boolean(editing)}
              onChange={(event) => setCode(event.target.value.toUpperCase())} />
            <TextField label={t("configuration.queues.displayName")} value={name}
              onChange={(event) => setName(event.target.value)} />
            <FormControl disabled={Boolean(editing)}><InputLabel>{t("configuration.queues.equipmentType")}</InputLabel>
              <Select label={t("configuration.queues.equipmentType")} value={equipmentTypeCode}
                onChange={(event) => setEquipmentTypeCode(event.target.value)}>
                <MenuItem value="">{t("configuration.common.none")}</MenuItem>
                <MenuItem value="ROLL">Roll</MenuItem>
                <MenuItem value="PALLET_TRUCK">{t("configuration.queues.palletTruck")}</MenuItem>
                <MenuItem value="FORKLIFT">{t("configuration.queues.forklift")}</MenuItem>
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>{t("configuration.queues.groupingMode")}</InputLabel>
              <Select label={t("configuration.queues.groupingMode")} value={groupingMode}
                onChange={(event) => setGroupingMode(event.target.value as
                  "SINGLE_HU" | "MULTI_HU")}>
                <MenuItem value="MULTI_HU">{t("configuration.queues.multiHu")}</MenuItem>
                <MenuItem value="SINGLE_HU">{t("configuration.queues.singleHu")}</MenuItem>
              </Select>
            </FormControl>
            <TextField label={t("configuration.queues.maxHu")} type="number" value={maxHu}
              disabled={groupingMode === "SINGLE_HU"}
              onChange={(event) => setMaxHu(event.target.value)} />
            <TextField label={`${t("configuration.queues.maxWeight")} (${massUnit})`} type="number"
              value={maxWeight}
              inputProps={{ min: massStep, step: massStep }}
              onChange={(event) => setMaxWeight(event.target.value)} />
            <TextField label={`${t("configuration.queues.maxVolume")} (${volumeUnit})`} type="number"
              value={maxVolume}
              inputProps={{ min: volumeStep, step: volumeStep }}
              onChange={(event) => setMaxVolume(event.target.value)} />
            <FormControl>
              <InputLabel>{t("configuration.queues.routing")}</InputLabel>
              <Select label={t("configuration.queues.routing")} value={routing}
                onChange={(event) => setRouting(event.target.value as
                  "NONE" | "PRIORITY" | "LOCATION_SEQUENCE")}>
                <MenuItem value="LOCATION_SEQUENCE">{t("configuration.queues.optimized")}</MenuItem>
                <MenuItem value="PRIORITY">{t("configuration.queues.businessPriority")}</MenuItem>
                <MenuItem value="NONE">{t("configuration.queues.noOptimization")}</MenuItem>
              </Select>
            </FormControl>
            {(editing?.code === "HEAVY" || code === "HEAVY") && (
              <TextField label={`${t("configuration.queues.heavyThreshold")} (${massUnit})`} type="number"
                value={heavyThreshold}
                inputProps={{ min: massStep, step: massStep }}
                onChange={(event) => setHeavyThreshold(event.target.value)} />
            )}
            {saveQueue.isError && (
              <Alert severity="error">{t("configuration.common.saveError")}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t("configuration.common.cancel")}</Button>
          <Button variant="contained" disabled={!warehouseId || !code.trim() || !name.trim()
            || Number(maxHu) < 1 || saveQueue.isPending}
            onClick={() => saveQueue.mutate()}>
            {t("configuration.common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

const equipmentTypes = {
  ROLL: { labelKey: "configuration.equipment.roll", prefix: "ROLL-" },
  TROLLET: { labelKey: "configuration.equipment.trollet", prefix: "TRL-" },
  PALLET_TRUCK: { labelKey: "configuration.equipment.palletTruck", prefix: "TPAL-" },
  FORKLIFT: { labelKey: "configuration.equipment.forklift", prefix: "CHEL-" }
} as const;

const equipmentReleasePolicies = [
  {
    key: "DELIVERY_EQUIPMENT_RELEASE_MODE",
    titleKey: "configuration.equipment.delivery",
    completionKey: "configuration.equipment.deliveryCompletion"
  },
  {
    key: "PICKING_EQUIPMENT_RELEASE_MODE",
    titleKey: "configuration.equipment.picking",
    completionKey: "configuration.equipment.pickingCompletion"
  },
  {
    key: "PUTAWAY_EQUIPMENT_RELEASE_MODE",
    titleKey: "configuration.equipment.putaway",
    completionKey: "configuration.equipment.putawayCompletion"
  }
] as const;

function EquipmentReleaseSettingsPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ["application-settings"],
    queryFn: loadApplicationSettings
  });
  const [modes, setModes] = useState<Record<string, string>>({});
  useEffect(() => {
    if (settings.data) setModes(Object.fromEntries(
      equipmentReleasePolicies.map(({ key }) => [
        key, settings.data.find((entry) => entry.key === key)?.value
          ?? "AUTO_ON_COMPLETION"
      ])
    ));
  }, [settings.data]);
  const save = useMutation({
    mutationFn: async () => {
      const changed = equipmentReleasePolicies.flatMap(({ key }) => {
        const setting = settings.data?.find((entry) => entry.key === key);
        return setting && modes[key] !== setting.value
          ? [updateApplicationSetting(key, modes[key], setting.version)] : [];
      });
      await Promise.all(changed);
    },
    onSuccess: async () => queryClient.invalidateQueries({
      queryKey: ["application-settings"]
    })
  });
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">{t("configuration.equipment.releaseTitle")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("configuration.equipment.releaseHelp")}
            </Typography>
          </Box>
          {equipmentReleasePolicies.map((policy) => {
            const setting = settings.data?.find(
              (entry) => entry.key === policy.key
            );
            if (!setting) return null;
            return <FormControl fullWidth key={policy.key}>
              <InputLabel>{t(policy.titleKey)}</InputLabel>
              <Select value={modes[policy.key] ?? setting.value}
                label={t(policy.titleKey)}
                onChange={(event) => setModes((current) => ({
                  ...current, [policy.key]: event.target.value
                }))}>
                <MenuItem value="AUTO_ON_COMPLETION">
                  {t("configuration.equipment.automaticRelease", { completion: t(policy.completionKey) })}
                </MenuItem>
                <MenuItem value="RETURN_TO_PARK_SCAN">
                  {t("configuration.equipment.parkRelease")}
                </MenuItem>
              </Select>
            </FormControl>;
          })}
          {settings.isError && (
            <Alert severity="error">{t("configuration.common.loadError")}</Alert>
          )}
          {save.isError && (
            <Alert severity="error">{t("configuration.common.saveError")}</Alert>
          )}
          <Box>
            <Button variant="contained" disabled={save.isPending
              || !equipmentReleasePolicies.some(({ key }) => {
                const setting = settings.data?.find((entry) => entry.key === key);
                return setting && modes[key] !== setting.value;
              })} onClick={() => save.mutate()}>
              {t("configuration.common.save")}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function HandlingEquipmentSettings({ readOnly = false }: { readOnly?: boolean }) {
  const { t, i18n } = useTranslation();
  const { configuration: measurements } = useMeasurementSettings();
  const locale = i18n.resolvedLanguage === "en" ? "en-US" : "fr-FR";
  const massUnit = measurementUnit("MASS", measurements);
  const massStep = measurementInputStep("MASS", measurements);
  const queryClient = useQueryClient();
  const equipment = useQuery({
    queryKey: ["handling-equipment"], queryFn: loadHandlingEquipment
  });
  const structure = useQuery({
    queryKey: ["warehouse-structure"], queryFn: loadWarehouseStructure
  });
  const warehouses = (structure.data ?? []).filter((node) => node.type === "WAREHOUSE");
  const [editing, setEditing] = useState<HandlingEquipment | null>(null);
  const [deleting, setDeleting] = useState<HandlingEquipment | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [warehouseId, setWarehouseId] = useState("");
  const [typeCode, setTypeCode] = useState<HandlingEquipment["typeCode"]>("ROLL");
  const [identifier, setIdentifier] = useState("ROLL-");
  const [capacity, setCapacity] = useState("80");
  const [status, setStatus] = useState<HandlingEquipment["status"]>("AVAILABLE");
  const open = (item?: HandlingEquipment) => {
    setEditing(item ?? null);
    setWarehouseId(item?.warehouseId ?? warehouses[0]?.id ?? "");
    setTypeCode(item?.typeCode ?? "ROLL");
    setIdentifier(item?.identifier ?? "ROLL-");
    setCapacity(measurementInputValue(item?.maxLoadKg ?? 80,
      "MASS", measurements));
    setStatus(item?.status ?? "AVAILABLE");
    setDialogOpen(true);
  };
  const save = useMutation({
    mutationFn: () => {
      const request = {
        warehouseId, typeCode, identifier: identifier.trim().toUpperCase(),
        maxLoadKg: toMetric(Number(capacity), "MASS", measurements), status
      };
      return editing
        ? updateHandlingEquipment(editing, request)
        : createHandlingEquipment(request);
    },
    onSuccess: async () => {
      setDialogOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["handling-equipment"] });
    }
  });
  const remove = useMutation({
    mutationFn: () => deleteHandlingEquipment(deleting!.id),
    onSuccess: async () => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ["handling-equipment"] });
    }
  });
  const expectedPrefix = equipmentTypes[typeCode].prefix;
  const validIdentifier = identifier.trim().toUpperCase().startsWith(expectedPrefix)
    && identifier.trim().length > expectedPrefix.length;
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}
            justifyContent="space-between" alignItems={{ sm: "center" }}>
            <Box>
              <Typography variant="h6">{t("configuration.equipment.title")}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t("configuration.equipment.description")}
              </Typography>
            </Box>
            {!readOnly && <Button variant="contained" onClick={() => open()}>{t("configuration.equipment.new")}</Button>}
          </Stack>
          {equipment.isError && <Alert severity="error">{t("configuration.equipment.loadError")}</Alert>}
          {[...(equipment.data ?? [])]
            .sort((left, right) => left.identifier.localeCompare(
              right.identifier, "fr", { numeric: true, sensitivity: "base" }
            ))
            .map((item) => (
            <Box key={item.id} sx={{
              display: "grid", gridTemplateColumns: {
                xs: "1fr", md: "1fr auto auto auto auto"
              },
              gap: 1.5, alignItems: "center", py: 1.25, borderTop: 1, borderColor: "divider"
            }}>
              <Box>
                <Typography fontWeight={800}>{item.identifier}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(equipmentTypes[item.typeCode].labelKey)} · {t(
                    "configuration.equipment.capacityValue", {
                      count: formatMeasurement(item.maxLoadKg, "MASS",
                        measurements, locale)
                    })}
                  {` · ${t("configuration.equipment.warehouseValue", { code: item.warehouseCode })}`}
                </Typography>
              </Box>
              <Chip label={item.typeName} variant="outlined" />
              <Chip label={item.status === "AVAILABLE" ? t("configuration.common.available")
                : item.status === "ASSIGNED" ? t("configuration.common.assigned")
                  : item.status === "IN_USE" ? t("configuration.common.inUse")
                    : item.status === "MAINTENANCE" ? t("configuration.common.maintenance") : t("configuration.common.outOfService")}
                color={item.status === "AVAILABLE" ? "success" : "default"} />
              {!readOnly && <Button variant="outlined" onClick={() => open(item)}>{t("configuration.common.configure")}</Button>}
              {!readOnly && <Button color="error" startIcon={<DeleteOutline />}
                onClick={() => setDeleting(item)}>{t("configuration.common.delete")}</Button>}
            </Box>
          ))}
        </Stack>
      </CardContent>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? t("configuration.equipment.configure", { identifier: editing.identifier }) : t("configuration.equipment.create")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl><InputLabel>{t("configuration.equipment.warehouse")}</InputLabel>
              <Select label={t("configuration.equipment.warehouse")} value={warehouseId}
                onChange={(event) => setWarehouseId(event.target.value)}>
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.code} · {warehouse.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl><InputLabel>{t("configuration.equipment.type")}</InputLabel>
              <Select label={t("configuration.equipment.type")} value={typeCode}
                onChange={(event) => {
                  const next = event.target.value as HandlingEquipment["typeCode"];
                  setTypeCode(next); setIdentifier(equipmentTypes[next].prefix);
                }}>
                {Object.entries(equipmentTypes).map(([code, type]) => (
                  <MenuItem key={code} value={code}>{t(type.labelKey)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label={t("configuration.equipment.identifier")} value={identifier}
              error={identifier.length > 0 && !validIdentifier}
              helperText={t("configuration.equipment.expectedFormat", { prefix: expectedPrefix })}
              onChange={(event) => setIdentifier(event.target.value.toUpperCase())} />
            <TextField label={`${t("configuration.equipment.capacity")} (${massUnit})`} type="number"
              value={capacity} inputProps={{ min: massStep, step: massStep }}
              onChange={(event) => setCapacity(event.target.value)} />
            <FormControl><InputLabel>{t("configuration.equipment.status")}</InputLabel>
              <Select label={t("configuration.equipment.status")} value={status}
                onChange={(event) => setStatus(event.target.value as HandlingEquipment["status"])}>
                <MenuItem value="AVAILABLE">{t("configuration.common.available")}</MenuItem>
                <MenuItem value="ASSIGNED">{t("configuration.common.assigned")}</MenuItem>
                <MenuItem value="IN_USE">{t("configuration.common.inUse")}</MenuItem>
                <MenuItem value="MAINTENANCE">{t("configuration.common.maintenance")}</MenuItem>
                <MenuItem value="OUT_OF_SERVICE">{t("configuration.common.outOfService")}</MenuItem>
              </Select>
            </FormControl>
            {save.isError && <Alert severity="error">
              {save.error instanceof Error ? save.error.message : t("configuration.common.saveError")}
            </Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t("configuration.common.cancel")}</Button>
          <Button variant="contained" disabled={!warehouseId || !validIdentifier
            || Number(capacity) <= 0 || save.isPending} onClick={() => save.mutate()}>
            {t("configuration.common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)}
        fullWidth maxWidth="xs">
        <DialogTitle>{t("configuration.equipment.deleteTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography>
              {t("configuration.equipment.deleteText", { identifier: deleting?.identifier })}
            </Typography>
            <Alert severity="warning">
              {t("configuration.equipment.deleteWarning")}
            </Alert>
            {remove.isError && <Alert severity="error">
              {remove.error instanceof Error
                ? remove.error.message : t("configuration.equipment.deleteError")}
            </Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>{t("configuration.common.cancel")}</Button>
          <Button color="error" variant="contained" disabled={remove.isPending}
            onClick={() => remove.mutate()}>
            {t("configuration.common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

function OutboundOrderSettingsPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ["application-settings"],
    queryFn: loadApplicationSettings
  });
  const setting = settings.data?.find(
    (entry) => entry.key === "OUTBOUND_ALLOCATION_MODE"
  );
  const [mode, setMode] = useState("SEMI_AUTOMATIC");
  useEffect(() => {
    if (setting) setMode(setting.value);
  }, [setting]);
  const save = useMutation({
    mutationFn: () => updateApplicationSetting(
      setting!.key, mode, setting!.version
    ),
    onSuccess: async () => queryClient.invalidateQueries({
      queryKey: ["application-settings"]
    })
  });
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">
              {t("configuration.outboundAllocation.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("configuration.outboundAllocation.description")}
            </Typography>
          </Box>
          {setting && (
            <FormControl fullWidth>
              <InputLabel>{t("configuration.fields.allocationMode")}</InputLabel>
              <Select value={mode} label={t("configuration.fields.allocationMode")}
                onChange={(event) => setMode(event.target.value)}>
                <MenuItem value="AUTOMATIC">
                  {t("configuration.outboundAllocation.automatic")}
                </MenuItem>
                <MenuItem value="SEMI_AUTOMATIC">
                  {t("configuration.outboundAllocation.semiAutomatic")}
                </MenuItem>
              </Select>
            </FormControl>
          )}
          <Alert severity={mode === "AUTOMATIC" ? "warning" : "info"}>
            {mode === "AUTOMATIC"
              ? t("configuration.outboundAllocation.automaticHelp")
              : t("configuration.outboundAllocation.semiAutomaticHelp")}
          </Alert>
          {save.isError && (
            <Alert severity="error">
              {save.error instanceof Error
                ? save.error.message : t("configuration.common.saveError")}
            </Alert>
          )}
          <Button variant="contained" sx={{ alignSelf: "flex-start" }}
            disabled={!setting || mode === setting.value || save.isPending}
            onClick={() => save.mutate()}>
            {t("configuration.common.save")}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function PickingSettingsPanel() {
  return (
    <Stack spacing={1.5}>
      <PickingPreparationHuSettingsPanel />
      <PickingWaveSequenceSettingsPanel />
      <PickingGroupingSettingsPanel />
    </Stack>
  );
}

function PickingPreparationHuSettingsPanel() {
  const { t } = useTranslation();
  const settings = useQuery({
    queryKey: ["application-settings"],
    queryFn: loadApplicationSettings
  });
  const preparationHuSettings = (settings.data ?? []).filter((entry) =>
    entry.key === "PICKING_PREPARATION_HU_DOCUMENT_GENERATION_ENABLED"
      || entry.key === "PICKING_PREPARATION_HU_SCAN_REQUIRED"
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">
              {t("configuration.pickingPreparationHu.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("configuration.pickingPreparationHu.description")}
            </Typography>
          </Box>
          {settings.isError && (
            <Alert severity="error">
              {t("configuration.pickingPreparationHu.loadError")}
            </Alert>
          )}
          {preparationHuSettings.map((setting) => (
            <PickingGroupingSettingRow key={setting.key} setting={setting}
              enabledLabel={t("configuration.common.yes")}
              disabledLabel={t("configuration.common.no")} />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

function GoodConsumptionSettingsPanel() {
  const { t } = useTranslation();
  const settings = useQuery({
    queryKey: ["application-settings"],
    queryFn: loadApplicationSettings
  });
  const setting = settings.data?.find((entry) =>
    entry.key === "PICKING_DEROGATED_LOT_VISUAL_CHECK_ENABLED"
  );
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">{t("configuration.goodConsumption.title")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("configuration.goodConsumption.description")}
            </Typography>
          </Box>
          {settings.isError && <Alert severity="error">
            {t("configuration.goodConsumption.loadError")}
          </Alert>}
          {setting && <PickingGroupingSettingRow setting={setting}
            enabledLabel={t("configuration.common.yes")}
            disabledLabel={t("configuration.common.no")} />}
        </Stack>
      </CardContent>
    </Card>
  );
}

function PickingWaveSequenceSettingsPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ["application-settings"],
    queryFn: loadApplicationSettings
  });
  const setting = settings.data?.find(
    (entry) => entry.key === "PICKING_WAVE_SEQUENCE_MODE"
  );
  const [mode, setMode] = useState("FLEXIBLE");
  useEffect(() => {
    if (setting) setMode(setting.value);
  }, [setting]);
  const save = useMutation({
    mutationFn: () => updateApplicationSetting(
      setting!.key, mode, setting!.version
    ),
    onSuccess: async () => queryClient.invalidateQueries({
      queryKey: ["application-settings"]
    })
  });
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">{t("configuration.pickingSequence.title")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("configuration.pickingSequence.description")}
            </Typography>
          </Box>
          {setting && (
            <FormControl fullWidth>
              <InputLabel>{t("configuration.fields.executionMode")}</InputLabel>
              <Select value={mode} label={t("configuration.fields.executionMode")}
                onChange={(event) => setMode(event.target.value)}>
                <MenuItem value="FLEXIBLE">
                  {t("configuration.pickingSequence.flexible")}
                </MenuItem>
                <MenuItem value="STRICT">
                  {t("configuration.pickingSequence.strict")}
                </MenuItem>
              </Select>
            </FormControl>
          )}
          <Alert severity={mode === "STRICT" ? "warning" : "info"}>
            {mode === "STRICT"
              ? t("configuration.pickingSequence.strictHelp")
              : t("configuration.pickingSequence.flexibleHelp")}
          </Alert>
          {save.isError && (
            <Alert severity="error">{t("configuration.common.saveError")}</Alert>
          )}
          <Button variant="contained" sx={{ alignSelf: "flex-start" }}
            disabled={!setting || mode === setting.value || save.isPending}
            onClick={() => save.mutate()}>
            {t("configuration.common.save")}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function PickingGroupingSettingsPanel() {
  const { t } = useTranslation();
  const settings = useQuery({
    queryKey: ["application-settings"],
    queryFn: loadApplicationSettings
  });
  const grouping = (settings.data ?? []).filter(
    (entry) => entry.category === "PICKING_GROUPING"
  );
  const master = grouping.find(
    (entry) => entry.key === "PICKING_ORDER_GROUPING_ENABLED"
  );
  const criteria = grouping.filter(
    (entry) => entry.key !== "PICKING_ORDER_GROUPING_ENABLED"
  );
  const enabled = master?.value === "true";

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">
              {t("configuration.pickingGrouping.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("configuration.pickingGrouping.description")}
            </Typography>
          </Box>
          {settings.isError && (
            <Alert severity="error">
              {t("configuration.pickingGrouping.loadError")}
            </Alert>
          )}
          {master && (
            <PickingGroupingSettingRow setting={master}
              enabledLabel={t("configuration.pickingGrouping.active")}
              disabledLabel={t("configuration.fields.oneOrderPerTour")} />
          )}
          <Box sx={{ opacity: enabled ? 1 : .58 }}>
            <Typography fontWeight={800} sx={{ mb: 1 }}>
              {t("configuration.pickingGrouping.reasons")}
            </Typography>
            <Stack>
              {criteria.map((setting) => (
                <PickingGroupingSettingRow key={setting.key}
                  setting={setting} disabled={!enabled}
                  enabledLabel={t("configuration.fields.criterionRequired")}
                  disabledLabel={t("configuration.fields.criterionIgnored")} />
              ))}
            </Stack>
          </Box>
          {!enabled && (
            <Alert severity="info">
              {t("configuration.pickingGrouping.disabledHelp")}
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function PickingGroupingSettingRow({
  setting, disabled = false, enabledLabel, disabledLabel
}: {
  setting: import("./api").ApplicationSetting;
  disabled?: boolean;
  enabledLabel: string;
  disabledLabel: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(setting.value === "true");
  useEffect(() => setEnabled(setting.value === "true"), [setting.value]);
  const save = useMutation({
    mutationFn: () => updateApplicationSetting(
      setting.key, String(enabled), setting.version
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["application-settings"] });
    }
  });
  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto auto" },
      gap: 1.5, alignItems: "center", py: 1,
      borderTop: 1, borderColor: "divider"
    }}>
      <Box>
        <Typography fontWeight={750}>{setting.displayName}</Typography>
        <Typography variant="body2" color="text.secondary">
          {setting.description}
        </Typography>
      </Box>
      <FormControlLabel
        control={<Switch checked={enabled} disabled={disabled}
          onChange={(event) => setEnabled(event.target.checked)} />}
        label={enabled ? enabledLabel : disabledLabel}
      />
      <Button variant="outlined"
        disabled={disabled || enabled === (setting.value === "true")
          || save.isPending}
        onClick={() => save.mutate()}>
        {t("configuration.common.save")}
      </Button>
      {save.isError && (
        <Alert severity="error" sx={{ gridColumn: "1 / -1" }}>
          {save.error instanceof Error
            ? save.error.message
            : "Enregistrement impossible. Rechargez la configuration."}
        </Alert>
      )}
    </Box>
  );
}

function ActivitySettingsPanel() {
  const { t } = useTranslation();
  const settings = useQuery({
    queryKey: ["application-settings"],
    queryFn: loadApplicationSettings
  });
  const navigationSettings = (settings.data ?? []).filter((entry) =>
    entry.category === "ACTIVITY_NAVIGATION"
  );

  return (
    <Stack spacing={1.5}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6">{t("configuration.activityNavigation.title")}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t("configuration.activityNavigation.description")}
              </Typography>
            </Box>
            {settings.isError && (
              <Alert severity="error">
                {t("configuration.activityNavigation.loadError")}
              </Alert>
            )}
            {navigationSettings.map((navigationSetting) => (
              <ActivityNavigationSettingRow
                key={navigationSetting.key}
                setting={navigationSetting}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

function ActivityNavigationSettingRow({
  setting
}: {
  setting: import("./api").ApplicationSetting;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(setting.value);
  useEffect(() => setValue(setting.value), [setting.value]);
  const save = useMutation({
    mutationFn: () => updateApplicationSetting(
      setting.key, value, setting.version
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["application-settings"] });
    }
  });
  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "minmax(280px, 1fr) 230px auto" },
      gap: 1.5, alignItems: "center", py: 1,
      borderTop: 1, borderColor: "divider"
    }}>
      <Box>
        <Typography fontWeight={750}>{setting.displayName}</Typography>
        <Typography variant="body2" color="text.secondary">
          {setting.description}
        </Typography>
      </Box>
      <FormControl size="small">
        <InputLabel>{t("configuration.fields.openingMode")}</InputLabel>
        <Select label={t("configuration.fields.openingMode")} value={value}
          onChange={(event) => setValue(event.target.value)}>
          <MenuItem value="DIRECT_ACTION">{t("configuration.activityNavigation.openDetail")}</MenuItem>
          <MenuItem value="TARGETED_LIST">{t("configuration.activityNavigation.showInList")}</MenuItem>
        </Select>
      </FormControl>
      <Button variant="outlined"
        disabled={value === setting.value || save.isPending}
        onClick={() => save.mutate()}>
        {t("configuration.common.save")}
      </Button>
      {save.isError && (
        <Alert severity="error" sx={{ gridColumn: "1 / -1" }}>
          {t("configuration.common.saveError")}
        </Alert>
      )}
    </Box>
  );
}

function QualitySettingsPanel() {
  const { t } = useTranslation();
  const settings = useQuery({
    queryKey: ["application-settings"], queryFn: loadApplicationSettings
  });
  const customerDecisionSetting = (settings.data ?? []).find((entry) =>
    entry.key === "CUSTOMER_DEROGATION_DECISION_DIRECT_APPLICATION_ENABLED"
  );
  const supplierDecisionSetting = (settings.data ?? []).find((entry) =>
    entry.key === "SUPPLIER_DEROGATION_DECISION_DIRECT_APPLICATION_ENABLED"
  );
  return (
    <Stack spacing={1.5}>
      <GoodConsumptionSettingsPanel />
      <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">{t("configuration.quality.title")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("configuration.quality.description")}
            </Typography>
          </Box>
          {settings.isError && <Alert severity="error">
            {t("configuration.quality.loadError")}
          </Alert>}
          {customerDecisionSetting && <PickingGroupingSettingRow
            setting={customerDecisionSetting}
            enabledLabel={t("configuration.quality.customerDirect.enabled")}
            disabledLabel={t("configuration.quality.customerDirect.disabled")}
          />}
          {supplierDecisionSetting && <PickingGroupingSettingRow
            setting={supplierDecisionSetting}
            enabledLabel={t("configuration.quality.supplierDirect.enabled")}
            disabledLabel={t("configuration.quality.supplierDirect.disabled")}
          />}
        </Stack>
      </CardContent>
      </Card>
    </Stack>
  );
}

function TransferSettingsPanel() {
  const { t } = useTranslation();
  const settings = useQuery({
    queryKey: ["application-settings"],
    queryFn: loadApplicationSettings
  });
  const transfers = (settings.data ?? []).filter(
    (entry) => entry.category === "TRANSFERS"
  );
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">{t("configuration.transfers.title")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("configuration.transfers.description")}
            </Typography>
          </Box>
          {settings.isError && (
            <Alert severity="error">
              {t("configuration.transfers.loadError")}
            </Alert>
          )}
          {transfers.map((setting) => (
            <TransferSettingRow key={setting.key} setting={setting} />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

function TransferSettingRow({
  setting
}: {
  setting: import("./api").ApplicationSetting;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(setting.value === "true");
  useEffect(() => setEnabled(setting.value === "true"), [setting.value]);
  const save = useMutation({
    mutationFn: () => updateApplicationSetting(
      setting.key, String(enabled), setting.version
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["application-settings"] });
    }
  });
  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto auto" },
      gap: 1.5, alignItems: "center", py: 1,
      borderTop: 1, borderColor: "divider"
    }}>
      <Box>
        <Typography fontWeight={750}>{setting.displayName}</Typography>
        <Typography variant="body2" color="text.secondary">
          {setting.description}
        </Typography>
      </Box>
      <FormControlLabel
        control={<Switch checked={enabled}
          onChange={(event) => setEnabled(event.target.checked)} />}
        label={enabled ? t("configuration.fields.taskRequired") : t("configuration.fields.immediateMove")}
      />
      <Button variant="outlined"
        disabled={enabled === (setting.value === "true") || save.isPending}
        onClick={() => save.mutate()}>
        {t("configuration.common.save")}
      </Button>
      {save.isError && (
        <Alert severity="error" sx={{ gridColumn: "1 / -1" }}>
          {t("configuration.common.saveError")}
        </Alert>
      )}
    </Box>
  );
}

function AllocationSettingsPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ["application-settings"],
    queryFn: loadApplicationSettings
  });
  const setting = settings.data?.find((entry) =>
    entry.key === "ALLOCATION_DEFAULT_RESERVATION_MINUTES"
  );
  const [minutes, setMinutes] = useState("30");

  useEffect(() => {
    if (setting) setMinutes(setting.value);
  }, [setting]);

  const save = useMutation({
    mutationFn: () => updateApplicationSetting(
      setting!.key, minutes, setting!.version
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["application-settings"] });
    }
  });

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">{t("configuration.stockReservation.title")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("configuration.stockReservation.description")}
            </Typography>
          </Box>
          <TextField type="number"
            label={t("configuration.fields.defaultReservation")}
            value={minutes}
            onChange={(event) => setMinutes(event.target.value)}
            inputProps={{ min: 1, max: 10080 }}
            helperText={setting?.description
              ?? t("configuration.fields.releaseUnused")} />
          {save.isSuccess && (
            <Alert severity="success">{t("configuration.common.saved")}</Alert>
          )}
          {save.isError && (
            <Alert severity="error">
              {t("configuration.common.saveError")}
            </Alert>
          )}
          <Box>
            <Button variant="contained"
              disabled={!setting || Number(minutes) < 1
                || Number(minutes) > 10080 || save.isPending}
              onClick={() => save.mutate()}>
              {t("configuration.common.save")}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function NumberingPolicyDialog({
  policy,
  onClose
}: {
  policy: NumberingPolicy | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const labels: Record<NumberingPolicy["documentType"], string> = {
    ASN: t("configuration.numbering.asn"),
    RECEIPT: t("configuration.numbering.receipt"),
    HANDLING_UNIT: t("configuration.numbering.hu"),
    INVENTORY_COUNT: t("configuration.numbering.inventoryCount")
  };
  const queryClient = useQueryClient();
  const [automatic, setAutomatic] = useState(false);
  const [prefix, setPrefix] = useState("");
  const [rangeStart, setRangeStart] = useState("1");
  const [rangeEnd, setRangeEnd] = useState("999999");
  const [padding, setPadding] = useState("6");
  const [validation, setValidation] = useState<NumberingValidation | null>(null);

  useEffect(() => {
    if (!policy) return;
    setAutomatic(policy.automatic);
    setPrefix(policy.prefix ?? "");
    setRangeStart(String(policy.rangeStart ?? 1));
    setRangeEnd(String(policy.rangeEnd ?? 999999));
    setPadding(String(policy.padding ?? 6));
    setValidation(null);
  }, [policy]);

  const request = {
    automatic,
    prefix: automatic ? prefix : null,
    rangeStart: automatic ? Number(rangeStart) : null,
    rangeEnd: automatic ? Number(rangeEnd) : null,
    padding: automatic ? Number(padding) : null
  };
  const save = useMutation({
    mutationFn: async () => {
      const result = await validateNumberingPolicy(policy!.documentType, request);
      setValidation(result);
      if (!result.valid) throw new Error("integrity");
      return activateNumberingPolicy(policy!.documentType, request);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["numbering-policies"] });
      onClose();
    }
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate();
  }

  return (
    <Dialog open={Boolean(policy)} onClose={onClose} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={submit}>
        <DialogTitle>
          {policy ? labels[policy.documentType] : t("configuration.numbering.title")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControlLabel
              control={<Switch checked={automatic}
                onChange={(event) => {
                  setAutomatic(event.target.checked);
                  setValidation(null);
                }} />}
              label={t("configuration.numbering.automaticGeneration")}
            />
            <TextField label={t("configuration.numbering.prefix")} value={prefix} disabled={!automatic}
              required={automatic}
              onChange={(event) => setPrefix(event.target.value.toUpperCase())} />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField type="number" label={t("configuration.numbering.rangeStart")} value={rangeStart}
                disabled={!automatic} required={automatic}
                inputProps={{ min: 0 }}
                onChange={(event) => setRangeStart(event.target.value)} />
              <TextField type="number" label={t("configuration.numbering.rangeEnd")} value={rangeEnd}
                disabled={!automatic} required={automatic}
                inputProps={{ min: 0 }}
                onChange={(event) => setRangeEnd(event.target.value)} />
              <TextField type="number" label={t("configuration.numbering.length")} value={padding}
                disabled={!automatic} required={automatic}
                inputProps={{ min: 1, max: 18 }}
                onChange={(event) => setPadding(event.target.value)} />
            </Stack>
            {automatic && (
              <TextField label={t("configuration.numbering.unreservedPreview")} disabled
                value={validation?.preview ?? policy?.preview ?? t("configuration.numbering.activationCalculation")} />
            )}
            {validation?.valid && (
              <Alert severity="success" icon={<CheckCircleOutline />}>
                {t("configuration.numbering.integritySuccess", { value: validation.effectiveNextValue })}
              </Alert>
            )}
            {validation && !validation.valid && (
              <Alert severity="error">
                {validation.errors.join(" · ")}
              </Alert>
            )}
            {save.isError && validation?.valid && (
              <Alert severity="error">
                {t("configuration.numberingExtra.changedDuringCheck")}
              </Alert>
            )}
            {save.isError && !validation && (
              <Alert severity="error">{t("configuration.common.operationError")}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("configuration.common.cancel")}</Button>
          <Button type="submit" variant="contained" disabled={save.isPending}>
            {t("configuration.numberingExtra.checkAndActivate")}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
